// Kagemusha hologram look: a shader-free flicker (alpha steps, sideways slips, dropouts) that turns violent while the
// double materialises and dissolves. Pure functions so tests can check them; render.ts draws the result.

/** Cheap deterministic noise in [0, 1). */
const rnd = (a: number, seed: number) => { const s = Math.sin(a * 91.7 + seed * 12.9898) * 43758.5453; return s - Math.floor(s); };

export const MATERIALISE_S = 0.4;
export const DISSOLVE_S = 0.5;

export interface DecoyFx {
  alpha: number;   // body opacity
  jx: number;      // whole-body sideways slip in px
  band: number;    // 0..1 position of the bright scan band down the body
  glitch: number;  // 0..1: how violent the effect is right now (spawn and despawn)
  bars: number;    // how many displaced slice bars to draw
}

/** t = render clock (s), id = owner id (de-syncs doubles), age = seconds since spawn, left = seconds until it ends. */
export function decoyFx(t: number, id: number, age: number, left: number): DecoyFx {
  const glitch = Math.max(0, 1 - age / MATERIALISE_S, 1 - left / DISSOLVE_S);
  const step = Math.floor(t * 18); // flicker runs in 18 Hz steps, like a bad projector
  const dropout = rnd(step, id) < 0.06 + glitch * 0.45;
  const base = dropout ? 0.2 : 0.74 + 0.16 * Math.sin(t * 9 + id);
  const slip = rnd(step + 7, id) < 0.05 + glitch * 0.6;
  return {
    alpha: base * (1 - glitch * 0.3),
    jx: slip ? Math.round((rnd(step + 3, id) - 0.5) * (3 + glitch * 8)) : 0,
    band: (t * 0.9 + id * 0.3) % 1,
    glitch,
    bars: Math.floor(glitch * 4 + (rnd(step + 11, id) < 0.15 ? 1 : 0)),
  };
}

/** Slice bars for the glitch effect: [relative y 0..1, relative height 0..1, sideways shift px]. */
export function glitchBars(t: number, id: number, n: number): [number, number, number][] {
  const step = Math.floor(t * 18), out: [number, number, number][] = [];
  for (let i = 0; i < n; i++) out.push([rnd(step + i * 5, id), 0.06 + rnd(step + i * 5 + 1, id) * 0.1, Math.round((rnd(step + i * 5 + 2, id) - 0.5) * 8)]);
  return out;
}
